import azertyKeyMap from '@/data/azertyKeys.json';
import qwertyKeyMap from '@/data/qwertyKeys.json';
import type { KeyboardLayout } from '@/graphql/graphql';
import { DEFAULT_KEYBOARD_LAYOUT } from '@/lib/constants';

export function convertKeysFromQwerty(
  keyboardLayout: KeyboardLayout,
  keysInQwerty: string[],
) {
  switch (keyboardLayout) {
    case 'QWERTY':
    default:
      return keysInQwerty;
    case 'AZERTY':
      return convertKeyboardKeys('QWERTY', keyboardLayout, keysInQwerty);
  }
}

export function getKeyMap(keyboardLayout: KeyboardLayout): KeyMap {
  switch (keyboardLayout) {
    case 'QWERTY':
    default:
      return qwertyKeyMap as KeyMap;
    case 'AZERTY':
      return azertyKeyMap as KeyMap;
  }
}

export function getKey(keyboardLayout: KeyboardLayout, key: string) {
  const keyMap = getKeyMap(keyboardLayout);

  let keyRow = -1,
    keyColumn = -1;

  keyMap.forEach((row, rowIndex) => {
    row.forEach((column, columnIndex) => {
      if (column.default === key) {
        keyRow = rowIndex;
        keyColumn = columnIndex;
      }
    });
  });

  return keyMap?.[keyRow]?.[keyColumn];
}

export function convertKeyboardKeys(
  keyboardLayoutFrom: KeyboardLayout,
  keyboardLayoutTo: KeyboardLayout,
  keysInFrom: string[],
): string[] {
  const keyMapFrom = getKeyMap(keyboardLayoutFrom);
  const keyMapTo = getKeyMap(keyboardLayoutTo);

  const keysInTo = keysInFrom.reduce<string[]>((accumulator, key) => {
    let keyRow = -1,
      keyColumn = -1;

    keyMapFrom.forEach((row, rowIndex) => {
      row.forEach((column, columnIndex) => {
        if (column.default === key) {
          keyRow = rowIndex;
          keyColumn = columnIndex;
        }
      });
    });

    // AZERTY has an extra key on next to the leftShift, so we add an extra to the column index
    const correctedKeyColumn =
      keyboardLayoutTo === 'AZERTY' && keyRow === 3 && keyColumn >= 1
        ? keyColumn + 1
        : keyColumn;

    const toKey = keyMapTo?.[keyRow]?.[correctedKeyColumn]?.default;

    return [...accumulator, toKey];
  }, []);

  return keysInTo;
}

export function lookupKeyboardLayout<T>(
  keyboardLayout: KeyboardLayout,
  lookup: Record<KeyboardLayout, T | undefined>,
): T {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return lookup?.[keyboardLayout] ?? lookup[DEFAULT_KEYBOARD_LAYOUT]!;
}
